/**
 * PD-LOC-01：在行程編輯中新增自訂地點（PRIVATE），可選立即公開。
 * 建立成功後以 linkedLocationId 當成 Activity locationNode。
 */
import { useId, useRef, useState } from 'react';
import { customLocationApi } from '../../api/customLocationApi';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';

function snowflakeId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

export default function CustomLocationCreatePanel({
  onCreated,
  disabled = false,
}) {
  const nameId = useId();
  const latId = useId();
  const lngId = useId();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [submitPublic, setSubmitPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);

  async function onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    if (submitting.current || disabled) return;
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!name.trim() || !latitude.trim() || !longitude.trim()
      || !Number.isFinite(lat) || !Number.isFinite(lng)
      || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      setError('請填寫名稱與有效座標。');
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const created = await customLocationApi.create({
        name: name.trim(),
        latitude: lat,
        longitude: lng,
      });
      const customId = snowflakeId(created?.locationId);
      const linkedLocationId = snowflakeId(created?.linkedLocationId);
      if (!customId || !linkedLocationId) throw new Error('missing location ids');
      if (submitPublic) {
        await customLocationApi.submitPublic(customId);
      }
      onCreated?.({
        locationId: linkedLocationId,
        displayName: name.trim(),
        canonicalName: name.trim(),
        locationType: 'POI',
        hasChildren: false,
      });
      setName('');
      setLatitude('');
      setLongitude('');
      setSubmitPublic(false);
      setOpen(false);
    } catch (cause) {
      setError(resolveSafeUserVisibleError(
        cause,
        () => '無法建立自訂地點，請稍後再試。',
        'travel.common.actionFailed',
      ));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="journey-planning__custom-location-open"
        data-testid="custom-location-open"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        新增自訂地點
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label="新增自訂地點"
      className="journey-planning__custom-location"
      data-testid="custom-location-form"
      onKeyDown={(event) => {
        // 僅接管文字欄位的 Enter；按鈕及輸入法組字保留原本操作。
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT'
          || event.target.type === 'checkbox') return;
        event.preventDefault();
        event.stopPropagation();
        if (!event.nativeEvent.isComposing && event.keyCode !== 229) onSubmit(event);
      }}
    >
      <p className="journey-planning__custom-location-title">新增自訂地點</p>
      <label htmlFor={nameId}>
        名稱
        <input
          id={nameId}
          value={name}
          disabled={busy || disabled}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </label>
      <label htmlFor={latId}>
        緯度
        <input
          id={latId}
          inputMode="decimal"
          value={latitude}
          disabled={busy || disabled}
          onChange={(e) => setLatitude(e.target.value)}
          required
        />
      </label>
      <label htmlFor={lngId}>
        經度
        <input
          id={lngId}
          inputMode="decimal"
          value={longitude}
          disabled={busy || disabled}
          onChange={(e) => setLongitude(e.target.value)}
          required
        />
      </label>
      <label className="journey-planning__custom-location-public">
        <input
          type="checkbox"
          checked={submitPublic}
          disabled={busy || disabled}
          onChange={(e) => setSubmitPublic(e.target.checked)}
          data-testid="custom-location-publish-toggle"
        />
        建立後立即公開（可被他人搜尋；無需等待審核）
      </label>
      {error ? <p role="alert">{error}</p> : null}
      <div className="journey-workspace__edit-actions">
        <button type="button" onClick={onSubmit} disabled={busy || disabled}>{busy ? '建立中…' : '建立'}</button>
        <button type="button" disabled={busy} onClick={() => setOpen(false)}>取消</button>
      </div>
    </div>
  );
}
